import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  ANALYTICS_V2_AUTH_REJECTED_EVENT,
  analyticsV2,
} from '../analytics/analyticsV2';

interface User {
  id?: string;
  name: string;
  surname: string;
  email: string;
  displayName: string;
  watchList?: string[];
}

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  login: (userData: User, token: string) => void;
  logout: () => void;
  updateWatchList: (watchList: string[]) => void;
}

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  user: null,
  login: () => {},
  logout: () => {},
  updateWatchList: () => {},
});

const safeStorage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Ignore storage failures (private mode / quota / restricted WebView).
    }
  },
  remove(key: string) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore storage failures (private mode / quota / restricted WebView).
    }
  },
};

const tokenIsFresh = (token: string): boolean => {
  try {
    const payload = jwtDecode<{ exp?: number }>(token);
    return (
      typeof payload.exp === 'number' &&
      payload.exp * 1000 > Date.now() + 5000
    );
  } catch {
    return false;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const lastTrackedWatchlistRef = useRef<string>('');

  // Al mount, carica i dati dal localStorage (se esistono)
  useEffect(() => {
    const token = safeStorage.get('token');
    const storedUser = safeStorage.get('user');
    if (token && storedUser && tokenIsFresh(token)) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser({ ...parsed, watchList: parsed.watchList || [] });
        setIsLoggedIn(true);
        analyticsV2.authenticationAvailable();
      } catch {
        safeStorage.remove('user');
        safeStorage.remove('token');
      }
    } else if (token || storedUser) {
      safeStorage.remove('user');
      safeStorage.remove('token');
    }
  }, []);

  useEffect(() => {
    const handleRejectedToken = () => {
      setUser(null);
      setIsLoggedIn(false);
      safeStorage.remove('token');
      safeStorage.remove('user');
    };
    window.addEventListener(
      ANALYTICS_V2_AUTH_REJECTED_EVENT,
      handleRejectedToken
    );
    return () =>
      window.removeEventListener(
        ANALYTICS_V2_AUTH_REJECTED_EVENT,
        handleRejectedToken
      );
  }, []);

  const login = (userData: User, token: string) => {
    if (!tokenIsFresh(token)) {
      throw new Error('Invalid or expired authentication token');
    }
    const normalizedUser = { ...userData, watchList: userData.watchList || [] };
    safeStorage.set('token', token);
    setUser(normalizedUser);
    setIsLoggedIn(true);
    safeStorage.set('user', JSON.stringify(normalizedUser));
    analyticsV2.track('login_succeeded', { method: 'application' });
    analyticsV2.authenticationAvailable();
  };

  const logout = () => {
    setUser(null);
    setIsLoggedIn(false);
    safeStorage.remove('token');
    safeStorage.remove('user');
  };

  const updateWatchList = (watchList: string[]) => {
    setUser(prev => {
      if (!prev) return prev;
      const previousWatchlist = (prev.watchList || []).map(value => value.toUpperCase());
      const nextWatchlist = (watchList || []).map(value => value.toUpperCase());
      const signature = JSON.stringify(nextWatchlist);
      if (lastTrackedWatchlistRef.current !== signature) {
        const added = nextWatchlist.filter(value => !previousWatchlist.includes(value));
        const removed = previousWatchlist.filter(value => !nextWatchlist.includes(value));
        if (added.length || removed.length) {
          analyticsV2.track('watchlist_updated', {
            action: added.length ? 'add' : 'remove',
            added,
            removed,
            size: nextWatchlist.length,
          });
        }
        lastTrackedWatchlistRef.current = signature;
      }
      const next = { ...prev, watchList: nextWatchlist };
      safeStorage.set('user', JSON.stringify(next));
      return next;
    });
  };

  return (
    <AuthContext.Provider
      value={{ isLoggedIn, user, login, logout, updateWatchList }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook per utilizzare facilmente il contesto
export const useAuth = () => useContext(AuthContext);
